import type { Route } from "./+types/newsroom.press-assets";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";
import { Link } from "react-router";
import { Download } from "lucide-react";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale]?.newsroom?.pressAssets?.meta;
  const title = t?.title ?? "Press Assets — Caratzon Newsroom";
  const description =
    t?.description ??
    "Brand logos, guidelines, and media resources for press and partners. Download Caratzon brand assets for editorial use.";
  return [
    { title },
    { name: "description", content: description },
    ...socialMeta({ title, description, pathname: "/newsroom/press-assets", locale }),
  ];
}

export default function PressAssetsPage() {
  const { t } = useT();
  const n = t?.newsroom?.pressAssets;

  const logoVariants = [
    { label: n?.logos?.primary ?? "Primary logo (light)", key: "newsroom/logo-primary" },
    { label: n?.logos?.dark ?? "Primary logo (dark)", key: "newsroom/logo-dark" },
    { label: n?.logos?.icon ?? "Icon only", key: "newsroom/logo-icon" },
    { label: n?.logos?.stacked ?? "Stacked", key: "newsroom/logo-stacked" },
  ];

  return (
    <MarketingLayout>
      {/* ── Hero ── */}
      <section className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <p
          className="issue-label text-[10px] uppercase tracking-[0.28em] text-muted-foreground"
          style={{ fontFamily: "var(--font-sans)" }}
        >
          {n?.hero?.issueLabel ?? "Press Assets"}
        </p>
        <div className="mt-3 w-8 border-t border-foreground/20" />
        <h1
          className="mt-4 font-display text-5xl md:text-6xl font-light italic tracking-tight leading-[1.1]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {n?.hero?.title ?? "Press Assets"}
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-md">
          {n?.hero?.subtitle ??
            "Brand resources for press, partners, and editorial use. Download logos and guidelines below."}
        </p>
        <div className="mt-10 border-t border-foreground/10" />
      </section>

      {/* ── Brand logos ── */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-6">
          {n?.logos?.heading ?? "Brand Logos"}
        </p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {logoVariants.map((variant) => (
            <a
              key={variant.key}
              href={`/resources/assets/render?key=${encodeURIComponent(variant.key)}`}
              download
              className="group flex flex-col items-center justify-center border border-foreground/10 p-8 transition-colors hover:border-foreground/20 hover:bg-muted/30"
            >
              <div className="mb-4 h-24 w-full bg-muted/50 flex items-center justify-center rounded">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {variant.label}
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Download
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* ── Brand guidelines ── */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="border-t border-foreground/10 pt-12">
          <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-6">
            {n?.guidelines?.heading ?? "Brand Guidelines"}
          </p>
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <h3 className="font-display text-lg font-medium tracking-tight mb-3">
                {n?.guidelines?.colorPalette ?? "Color Palette"}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {n?.guidelines?.colorNotes ??
                  "Primary brand accent: Teal Sapphire (#005F6B). Deep India ink for text. Warm cream vellum for backgrounds. Gold leaf reserved for decorative use only."}
              </p>
              <div className="flex gap-3">
                <div
                  className="h-12 w-12 rounded border border-foreground/10"
                  style={{ backgroundColor: "#005F6B" }}
                  title="Teal Sapphire"
                />
                <div
                  className="h-12 w-12 rounded border border-foreground/10"
                  style={{ backgroundColor: "oklch(0.085 0.004 50)" }}
                  title="India Ink"
                />
                <div
                  className="h-12 w-12 rounded border border-foreground/10"
                  style={{ backgroundColor: "oklch(0.978 0.008 90)" }}
                  title="Cream Vellum"
                />
              </div>
            </div>
            <div>
              <h3 className="font-display text-lg font-medium tracking-tight mb-3">
                {n?.guidelines?.typography ?? "Typography"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {n?.guidelines?.typographyNotes ??
                  "Display: Cormorant Garamond, Cormorant SC. Body: Inter. Use display fonts for headlines and editorial accents; Inter for UI and body copy."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Press contact ── */}
      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="border-t border-foreground/10 pt-12">
          <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-6">
            {n?.contact?.heading ?? "Press Contact"}
          </p>
          <p className="text-muted-foreground mb-4">
            {n?.contact?.description ??
              "For media inquiries, press kits, and interview requests:"}
          </p>
          <a
            href="mailto:press@caratzon.com"
            className="font-display text-xl font-medium text-teal hover:underline"
          >
            press@caratzon.com
          </a>
          <p className="mt-6 text-sm text-muted-foreground">
            <Link to="/contact" className="text-foreground hover:text-teal transition-colors">
              {n?.contact?.contactLink ?? "General inquiries →"}
            </Link>
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
